import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import { toast } from "react-toastify";
import { getAdvertiserTypes, createAdAccount, getBillingProfile } from "../../hook/useAdsApi";
import { ADVERTISER_TYPES_FALLBACK, getAdvertiserTypeIcon } from "./adsConstants";

export default function AddAdAccount() {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Shown as a persistent inline banner, not just a toast, since createAdAccount rejects
  // (no row is ever written to the DB) when the account isn't eligible for the chosen type -
  // there's nothing to "show as pending" because nothing was created.
  const [error, setError] = useState(null);

  useEffect(() => {
    getAdvertiserTypes()
      .then((list) => {
        // The API only returns {id, code, name, status} - borrow the descriptive
        // subtext from the fallback list (matched by code) for display purposes.
        const withDescriptions = list.map((type) => ({
          ...type,
          description: ADVERTISER_TYPES_FALLBACK.find((f) => f.key === type.code)?.description,
        }));
        setTypes(withDescriptions.length > 0 ? withDescriptions : ADVERTISER_TYPES_FALLBACK);
        if (withDescriptions.length > 0) setSelectedTypeId(withDescriptions[0].id);
      })
      .catch(() => setTypes(ADVERTISER_TYPES_FALLBACK));
  }, []);

  const handleNext = async () => {
    const type = types.find((t) => (t.id ?? t.key) === selectedTypeId);
    if (!type) {
      toast.error("Select an account type to continue");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const account = await createAdAccount(
        type.id ? { advertiser_type_id: type.id } : { advertiser_type: type.key }
      );
      toast.success("Ad account created");

      // First-time sellers need a billing profile before they can fund/run campaigns - route
      // them through setup first instead of straight to campaign creation.
      const billingProfile = await getBillingProfile().catch(() => ({ configured: true }));
      if (!billingProfile.configured) {
        navigate("/ads/billing/model?mode=setup", { state: { adAccount: account } });
      } else {
        navigate("/ads/create", { state: { adAccount: account } });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create ad account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                ←
              </button>
            </div>

            {/* Exact type/colors from Figma's "Add New Account" screen (read-only access). */}
            <h1 className="text-[22px] font-semibold text-black capitalize">Add New Account</h1>
            <p className="text-sm text-[#747474] mt-2">Select Account Type</p>
            <div className="h-1 bg-[#d9d9d9] rounded-full my-4" />

            <div className="space-y-4 mb-8">
              {types.map((type) => {
                const id = type.id ?? type.key;
                const iconSrc = getAdvertiserTypeIcon(type.code ?? type.key);
                const selected = selectedTypeId === id;
                return (
                  <label
                    key={id}
                    className={`flex items-center gap-4 bg-white border border-[#98911f] rounded-[20px] px-5 py-4 cursor-pointer ${
                      selected ? "shadow-[0_0_0_5px_rgba(59,182,76,0.2)]" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="advertiserType"
                      checked={selected}
                      onChange={() => {
                        setSelectedTypeId(id);
                        setError(null);
                      }}
                      className="sr-only"
                    />
                    {iconSrc && <img src={iconSrc} alt="" className="w-8 h-8 shrink-0" />}
                    <div>
                      <p className="text-base font-semibold text-black">{type.name}</p>
                      <p className="text-[10px] text-[#747474] mt-1">{type.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-[#fef2f2] border border-[#ba1a1a] text-[#ba1a1a] rounded-2xl px-4 py-3 mb-6 text-sm">
                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                <p>{error}</p>
              </div>
            )}

            <button
              type="button"
              disabled={submitting}
              onClick={handleNext}
              className="bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-10 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)] disabled:opacity-50"
            >
              {submitting ? "Creating..." : "Next"}
            </button>
          </div>
        </main>
      </main>
    </div>
  );
}
